import dynamic from "next/dynamic";
import { useAppContext } from "@context/app";
import Logo from "./logo";
import Search from "./search";
import CartButton from "./cart-button";
import MyCourseButton from "./my-courses-button";
import MegaMenuPopover from "./megamenu-popover";
import { useState } from "react";
import { useRouter } from "next/router";
import FestivalBanner from "../FestivalBanner";
import classNames from "classnames";
import { useLayoutContext } from "@context/layout";
import ProfileButton from "./profile-button";
import { IMegamenuInput } from "features/home/helpers/types";

const AdminHeader = dynamic(() => import("./admin-header"), { ssr: false });

const AppBanner = dynamic(() => import("@shared/components/app-banner"), {
  ssr: false,
});
const SubscriptionBanner = dynamic(
  () => import("@shared/components/subscription-banner"),
  { ssr: false }
);

interface Props {
  megamenu?: IMegamenuInput;
  hideFestivalBanner: boolean;
  removeAdminHeader?: boolean;
  isAdminProp?: boolean;
  className?: string;
}

const pathToCheck = [
  "/checkout",
  "/courses",
  "/podcasts",
  "/webinar",
  "/exams",
  "/login",
  "/register",
  "/my-account",
  "/cart",
  "blinds",
];

const pagesWithJustPageTitle = [
  "/program/[slug]",
  "/videos/[slug]",
  "/cart",
  "/checkout",
  "/verify/[slug]",
  "/faradars-for-organizations",
  "/subscription/checkout",
];
const hideSubscriptionPath = [
  "/subscription",
  "/subscription/checkout",
  "/checkout",
  "/courses",
  "/podcasts",
  "/webinar",
  "/exams",
  "/login",
  "/register",
  "/my-account",
  "/cart",
  "blinds",
];

const Header: React.FC<Props> = ({
  megamenu,
  hideFestivalBanner,
  isAdminProp,
}) => {
  const { showFestivalBanner, hideTopbar } = useAppContext();
  const router = useRouter();
  const { userData, subscriptionData, fetstivalBanner } = useLayoutContext();

  const isInstructorPage = ["/instructors/[slug]"].includes(router.pathname);
  const hideTopBar =
    pagesWithJustPageTitle.includes(router.pathname) || hideTopbar;

  const [showMegaMenu, setShowMegaMenu] = useState(false);

  const renderFetsivalBanner = hideFestivalBanner
    ? false
    : typeof showFestivalBanner !== "undefined"
      ? showFestivalBanner
      : fetstivalBanner?.is_active;

  const isAdmin =
    typeof isAdminProp !== "undefined" ? isAdminProp : userData?.user?.isAdmin;

  const hasActiveSubscription = subscriptionData?.is_active;

  const isHideAppBanner = pathToCheck.some((path) =>
    router.asPath.includes(path)
  );

  const isHideSubscriptionBanner = hideSubscriptionPath.includes(
    router.pathname
  );

  const shouldShowAppBanner = hasActiveSubscription && !isHideAppBanner;

  const shouldShowSubscriptionBanner =
    !hasActiveSubscription && !isHideSubscriptionBanner;
  return (
    <>
      <header
        data-site-header
        className="sticky top-0 transition duration-150 ease-linear right-0 z-layout w-full flex-col"
      >
        <AdminHeader isAdmin={isAdmin} />
        {renderFetsivalBanner && <FestivalBanner />}

        <nav
          className={classNames(
            "pointer-events-auto bg-white shadow-sm",
            hideTopBar ? "md:block hidden" : "",
            isInstructorPage ? "hidden fds-l:block" : ""
          )}
        >
          <div className="flex h-fds-60 items-center gap-2 md:gap-3 w-full max-w-[1920px] px-4 md:px-5 lg:px-16 mx-auto">
            <Logo />
            <MegaMenuPopover
              megamenu={megamenu}
              showMegaMenu={showMegaMenu}
              setShowMegaMenu={setShowMegaMenu}
            />
            <Search isAdmin={isAdmin} open={!showMegaMenu} />
            <MyCourseButton />
            <ProfileButton />
            {!hasActiveSubscription && (
              <>
                <div className="w-px bg-fds-disable h-6 hidden md:block" />
                <div className=" hidden md:block">
                  <CartButton />
                </div>
              </>
            )}
          </div>
        </nav>
      </header>
      {shouldShowAppBanner ? (
        <AppBanner />
      ) : shouldShowSubscriptionBanner ? (
        <SubscriptionBanner />
      ) : null}
    </>
  );
};

export default Header;
